import type { ReactNode } from 'react'
import { Spinner, Text, VStack } from '@chakra-ui/react'
import { useFollowUpSummary } from '../../../hooks/useFollowUp'
import { extractApiMessage } from '../../../lib/apiError'
import { FollowUpSectionCard } from './FollowUpSectionCard'
import { sectionSlotKey, type SectionSlots } from './sectionSlots'
import type {
  CampaignDisplayMode, FollowUpItemRowDto, FollowUpKind, FollowUpKindCountDto,
} from '../../../types/followUp'

type BuiltInKind = Exclude<FollowUpKind, 'Custom'>

const SECTION_TITLES: Record<BuiltInKind, string> = {
  Chase: 'Chase to vote',
  SignDropOff: 'Sign drop-offs',
  CallBack: 'Call-backs',
}

const ORDER: Record<CampaignDisplayMode, BuiltInKind[]> = {
  FinalPush: ['Chase', 'SignDropOff', 'CallBack'],
  Standard: ['SignDropOff', 'CallBack', 'Chase'],
}

const emptyCounts = (kind: FollowUpKind, label: string): FollowUpKindCountDto => ({
  kind, customKindId: null, label, mode: kind === 'CallBack' ? 'Call' : 'Route',
  open: 0, done: 0, couldnt: 0, cancelled: 0, superseded: 0, declined: 0,
})

const totalItems = (c: FollowUpKindCountDto) =>
  c.open + c.done + c.couldnt + c.cancelled + c.superseded + c.declined

export interface FollowUpTabProps {
  campaignId: string
  effectiveMode: CampaignDisplayMode
  /** CF-1 contract: the ONLY extension point for section actions (P2/P4/P5) — merge with mergeSectionSlots. */
  sectionSlots?: SectionSlots
  /** CF-1 contract: the ONLY extension point for per-row actions; passed to every section's list. */
  rowActions?: (row: FollowUpItemRowDto) => ReactNode
}

export function FollowUpTab({ campaignId, effectiveMode, sectionSlots, rowActions }: FollowUpTabProps) {
  const { data: summary, isLoading, error } = useFollowUpSummary(campaignId)

  if (isLoading) {
    return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  }
  if (!!error || !summary) {
    return <Text color="danger.fg">{extractApiMessage(error, 'Could not load follow-ups.')}</Text>
  }

  const builtIns = ORDER[effectiveMode].map(kind => ({
    kind: kind as FollowUpKind,
    customKindId: undefined as string | undefined,
    title: SECTION_TITLES[kind],
    counts: summary.kinds.find(k => k.kind === kind) ?? emptyCounts(kind, SECTION_TITLES[kind]),
  }))
  const customs = summary.kinds
    .filter(k => k.kind === 'Custom' && !!k.customKindId && totalItems(k) > 0)
    .map(k => ({ kind: k.kind, customKindId: k.customKindId!, title: k.label, counts: k }))

  return (
    <VStack gap={4} align="stretch">
      {[...builtIns, ...customs].map(s => (
        <FollowUpSectionCard
          key={sectionSlotKey(s.kind, s.customKindId)}
          campaignId={campaignId}
          kind={s.kind}
          customKindId={s.customKindId}
          title={s.title}
          counts={s.counts}
          actions={sectionSlots?.[sectionSlotKey(s.kind, s.customKindId)]}
          rowActions={rowActions}
        />
      ))}
    </VStack>
  )
}
